import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Label } from 'components/Typography';
import Input from '../Input';
import CountryCodes from './CountryCodes';
import {
  Wrapper,
  InputContainer,
  Plus,
  StyledTooltip,
  Optional,
  CountryFlag,
  ErrorMessage,
  CheckIcon
} from './styled';

/**
 * Phone number input
 * + sign hard-coded (cannot be added)
 * leading zeros in country code replaced with ""
 * Phone country code and phone number separated
 *    ------   --------------
 * + | 358  | | 5012345667   |
 *    ------   --------------
 */

const COUNTRY_CODE_MAX_LENGTH = 4;

const PhoneInput = ({
  name,
  label,
  variant,
  formik,
  size,
  spacing,
  tooltip,
  disabled,
  optional
}) => {
  const countryCodeInput = useRef();
  const { phoneCountryCode, phoneNumber } = formik.values;

  const [isFlagVisible, setIsFlagVisible] = useState(false);
  const [flagCountryCode, setFlagCountryCode] = useState(null);

  useEffect(() => {
    if (phoneCountryCode !== '') {
      const obj = CountryCodes.find((o) => o.dial_code === `+${phoneCountryCode}`);
      setFlagCountryCode(obj ? obj.code : null);
      setIsFlagVisible(!!obj);
    }
  }, [phoneCountryCode]);

  const renderErrorMessage = () => {
    if (formik.touched.phoneCountryCode && formik.errors.phoneCountryCode) {
      return <ErrorMessage>{formik.errors.phoneCountryCode}</ErrorMessage>;
    }

    if (formik.touched.phoneNumber && formik.errors.phoneNumber) {
      return <ErrorMessage>{formik.errors.phoneNumber}</ErrorMessage>;
    }

    return null;
  };

  const renderLabel = (
    <Label htmlFor={name} variant={variant} size={size} spacing={spacing}>
      {label}
      {tooltip && <StyledTooltip variant={variant}>{tooltip}</StyledTooltip>}
      {optional && <Optional>- optional</Optional>}
      {formik && (
        <>
          {renderErrorMessage()}
          {!formik.errors.phoneCountryCode &&
            !formik.errors.phoneNumber &&
            formik.dirty && <CheckIcon />}
        </>
      )}
    </Label>
  );

  const handleChange = (e) => {
    // remove leading zeros
    e.target.value = e.target.value.replace(/^0+/, '');
    // remove non digits
    e.target.value = e.target.value.replace(/\D+/g, '');

    if (e.target.name === 'phoneCountryCode') {
      if (e.target.value && e.target.value.length > COUNTRY_CODE_MAX_LENGTH) {
        e.target.value = e.target.value.slice(0, COUNTRY_CODE_MAX_LENGTH);
      }

      const obj = CountryCodes.find((o) => o.dial_code === `+${e.target.value}`);
      setFlagCountryCode(obj ? obj.code : null);
      setIsFlagVisible(!!obj);
    } else {
      const phoneNumberMaxLength = phoneCountryCode ? 14 - phoneCountryCode?.length : 12;
      if (e.target.value && e.target.value.length > phoneNumberMaxLength) {
        e.target.value = e.target.value.slice(0, phoneNumberMaxLength);
      }
    }

    formik.handleChange(e);
  };

  const handleKeyPress = (e) => {
    if (e.target.value < 1 && e.keyCode === 8) {
      countryCodeInput.current.focus();
    }
  };

  return (
    <>
      {label && renderLabel}
      <Wrapper size={size}>
        {isFlagVisible && (
          <CountryFlag
            isFlagVisible={isFlagVisible}
            position="absolute"
            code={flagCountryCode}
            size={16}
          />
        )}
        <InputContainer isFlagVisible={isFlagVisible} disabled={disabled}>
          <Plus variant={variant}>+</Plus>
          <Input
            type="tel-country-code"
            autoComplete="tel-country-code"
            variant={variant}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            value={phoneCountryCode}
            inputMode="numeric"
            name="phoneCountryCode"
            ref={countryCodeInput}
          />
          <Input
            type="tel-national"
            autoComplete="tel-national"
            variant={variant}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            onKeyDown={handleKeyPress}
            inputMode="numeric"
            value={phoneNumber}
            name="phoneNumber"
          />
        </InputContainer>
      </Wrapper>
    </>
  );
};

PhoneInput.propTypes = {
  formik: PropTypes.object,
  name: PropTypes.string,
  optional: PropTypes.bool,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  variant: PropTypes.string,
  spacing: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'large', 'extralarge'])
};

PhoneInput.defaultProps = {
  label: '',
  tooltip: '',
  optional: false,
  variant: 'dark',
  size: 'medium',
  spacing: 'normal'
};

export default PhoneInput;
